import type { ParsedSystemRow } from '@/lib/excel';
import type { InitializeProcessDraft } from '@/components/bulk-upload';
import type { OrgFunctionSurfaceDrafts } from '../steps/OrgFunctionSurfaceStep';
import { serializeRowData } from '../utils/classifyAnchorDrafts';
import type { ParsedDepartment, ParsedUser } from '../utils/excelParsers';

/**
 * Per-step flush baselines the autosave engine and Weiter diff against.
 * Anchor-classified steps keep the row snapshot the last flush (or the
 * freeze-at-first-edit seed capture) was based on; surfaces keep a serialized
 * snapshot of what was last sent. A step without an entry has never been
 * flushed this visit.
 */
export interface WizardFlushBaselines {
  departments?: ParsedDepartment[];
  users?: ParsedUser[];
  /** Serialized surface snapshot (see serializeSurface). */
  functions?: string;
  teams?: string;
  systems?: ParsedSystemRow[];
  processes?: InitializeProcessDraft[];
}

// entityId is included on top of the classifier's row serialization: it flows
// onto the wire as `_id`, so gaining one is a payload change. Exported as the
// identity of a surface row inside a snapshot (flushedSurfaceMembershipKeys).
export function serializeSurfaceRow(row: { data: object; entityId?: string }): string {
  return JSON.stringify([row.entityId ?? '', serializeRowData(row.data)]);
}

/**
 * Stable snapshot of a Funktionen/Teams surface draft — the surface steps'
 * dirty check compares this against the baseline snapshot of the last flush.
 * Deliberately excludes the pending-removal queues: removals never flush
 * silently, so they must not read as flushable dirt (isSurfaceStepDirty
 * accounts for them separately).
 */
export function serializeSurface(
  drafts: Pick<OrgFunctionSurfaceDrafts, 'rows' | 'memberships'>
): string {
  return JSON.stringify({
    rows: drafts.rows.map(serializeSurfaceRow),
    memberships: drafts.memberships.map(serializeSurfaceRow)
  });
}

/**
 * Membership rows the last silent flush already persisted. Memberships carry
 * no server id (linking returns none), so identity falls back to the
 * serialized rows captured in the flush baseline snapshot — used to decide
 * which deleted membership rows must queue a `membershipRemovals` entry
 * (never-flushed rows only exist client-side and just disappear).
 */
export function flushedSurfaceMembershipKeys(snapshot: string | undefined): Set<string> {
  if (!snapshot) return new Set();
  const { memberships } = JSON.parse(snapshot) as { memberships: string[] };
  return new Set(memberships);
}

/**
 * A cleared anchor table: "Liste leeren" (or deleting every row) emptied the
 * draft while a flush baseline survives, so the empty draft means "delete
 * everything the baseline still holds" (committed on Weiter) — NOT the
 * untouched seed fallback. Without a baseline an empty draft stays the
 * untouched case. Protected rows (locked-identity members the clear cannot
 * remove) don't count on either side: cleared = no replaceable rows left
 * while the baseline still holds replaceable ones.
 */
export function isClearedAnchorDraft(
  draftRows: readonly { entityId?: string }[],
  baseline: readonly { entityId?: string }[] | undefined,
  protectedIds?: ReadonlySet<string>
): boolean {
  const replaceable = (rows: readonly { entityId?: string }[]) =>
    protectedIds
      ? rows.filter((row) => row.entityId === undefined || !protectedIds.has(row.entityId))
      : rows;
  return (
    replaceable(draftRows).length === 0 &&
    baseline !== undefined &&
    replaceable(baseline).length > 0
  );
}

/**
 * The ONE rule for what an anchor draft diffs against: the flush baseline
 * when one exists (frozen at first edit, moved by silent flushes), else the
 * done-gated seed fallback, else undefined (fresh import — everything is a
 * create). An EMPTY draft never adopts the seed fallback: untouched (no
 * explicit baseline) must classify as unchanged, while a cleared table
 * (isClearedAnchorDraft) diffs its explicit baseline into a delete-all
 * delta. The dirty check, the silent flush, and the authoritative Weiter
 * classify must all use this, or their verdicts drift apart — the classifier
 * drift that produces spurious surgical deletes.
 */
export function effectiveAnchorBaseline<TRow>(
  baseline: TRow[] | undefined,
  stepDone: boolean,
  seeds: TRow[],
  draftRows: readonly TRow[]
): TRow[] | undefined {
  if (draftRows.length === 0) return baseline;
  return baseline ?? (stepDone ? seeds : undefined);
}
